'use client'

import { configureStore, type Middleware } from '@reduxjs/toolkit'
import { useDispatch, useSelector, useStore } from 'react-redux'
import {
  getStoredState as defaultGetStoredState,
  type PersistConfig,
  persistReducer,
  persistStore,
} from 'redux-persist'
import storage from 'redux-persist/lib/storage'

import { baseApiSlice } from '@/legacy-api/base-api-slice'
import { rootReducer } from '@/reducers/root'

const apiMiddleware: Middleware[] = [baseApiSlice.middleware]

// Custom getStoredState function to handle errors when restoring persisted state
// If loading the persisted state throws an error, clear local storage and fall back to initial state
const getStoredState: typeof defaultGetStoredState = async (config) => {
  try {
    // Use the default getStoredState to reuse its logic
    return await defaultGetStoredState(config)
  } catch {
    // Clear the persisted state from local storage on failure
    // Redux-persist stores data with the key 'persist:${config.key}'
    const persistKey = `persist:${config.key}`
    try {
      storage.removeItem(persistKey)
    } catch {
      // If clearing storage also fails, continue anyway
    }
    // Return undefined to fall back to initial state instead of throwing
    // Type assertion needed because redux-persist accepts undefined to fall back to initial state
    return undefined as unknown as ReturnType<typeof defaultGetStoredState>
  }
}

// Configure persistence for model builder
// Type assertion needed because getStoredState is a valid but optional property
// that redux-persist supports but TypeScript types don't fully reflect
const persistConfig = {
  key: 'root',
  version: 1,
  storage,
  whitelist: ['modelBuilder'], // Only persist modelBuilder state
  getStoredState,
} as PersistConfig<ReturnType<typeof rootReducer>>

const persistedReducer = persistReducer(persistConfig, rootReducer)

export const makeStore = () => {
  const store = configureStore({
    reducer: persistedReducer,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        // Can enable these 3 checks in local development to catch issues
        // Choosing to disable them for now to make local performance
        // match production performance.
        // See https://redux-toolkit.js.org/api/getDefaultMiddleware
        serializableCheck: false,
        immutableCheck: false,
        actionCreatorCheck: false,
        thunk: true, // https://github.com/reduxjs/redux-thunk
      }).concat(apiMiddleware),
  })

  return store
}

export const persistor = (store: AppStore) => persistStore(store)

export type AppStore = ReturnType<typeof makeStore>
export type RootState = ReturnType<AppStore['getState']>
export type AppDispatch = AppStore['dispatch']

export const useAppDispatch = useDispatch.withTypes<AppDispatch>()
export const useAppSelector = useSelector.withTypes<RootState>()
export const useAppStore = useStore.withTypes<AppStore>()
